/**
 * FIR-7631 — Customizable inbox labels.
 *
 * Company-scoped labels applied to reply threads, backed by the
 * `/emailbison/reply-labels/` CRUD endpoints and the per-thread
 * `toggle-label` action (see `replies_api.ts`). Label rows arrive on each
 * thread from the reply-threads list serializer, so pills render straight
 * off `reply.labels`; this module owns the label-set state and the apply UI.
 *
 * Consumed by CampaignRepliesTab.tsx in three spots:
 *   - top-bar Labels filter (via the existing InlineSelectFilter)
 *   - per-thread apply UI in the classification bottom bar (<ThreadLabelsInline>)
 *   - label pills on each list row (<LabelPill>)
 * and by the Settings "Reply Labels" tab (ReplyLabelTable), which shares the
 * same query keys so both surfaces stay in sync.
 */
import {
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { cn } from 'components/UI/cn'
import { useMountEffect } from 'hooks/useMountEffect'
import Checkbox from 'components/Checkbox/checkbox'
import FbButton from 'components/FbUI/FbButton'
import { FilterSearchInput } from 'components/Filters/components/FilterSearchInput'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { PiPlus, PiCheck, PiPencilSimple, PiTrash, PiX } from 'react-icons/pi'
import {
  type ApiReplyLabel,
  useCreateReplyLabel,
  useDeleteReplyLabel,
  useGetReplyLabels,
  useToggleThreadLabel,
  useUpdateReplyLabel,
} from 'features/campaigns/replies_api'

// Green (success/primary), amber-orange (warning/orange), and gray are omitted
// on purpose — those read as the Interested / Flagged / Not-Interested badge
// colors, so a label in them would be confusable with a classification state.
// Mirrors ReplyLabelColor in server/emailbison/models/reply_label.py — keep
// the two in sync.
export type LabelColorKey =
  | 'error'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'violet'
  | 'purple'
  | 'pink'
  | 'rose'
  | 'teal'

export const LABEL_PALETTE: Record<
  LabelColorKey,
  { bg: string; text: string; dot: string }
> = {
  error: { bg: 'bg-ds2026-badge-error-bg', text: 'text-ds2026-badge-error-text', dot: 'bg-ds2026-badge-error-icon' }, // prettier-ignore
  cyan: { bg: 'bg-ds2026-badge-cyan-bg', text: 'text-ds2026-badge-cyan-text', dot: 'bg-ds2026-badge-cyan-icon' }, // prettier-ignore
  sky: { bg: 'bg-ds2026-badge-sky-bg', text: 'text-ds2026-badge-sky-text', dot: 'bg-ds2026-badge-sky-icon' }, // prettier-ignore
  blue: { bg: 'bg-ds2026-badge-blue-bg', text: 'text-ds2026-badge-blue-text', dot: 'bg-ds2026-badge-blue-icon' }, // prettier-ignore
  violet: { bg: 'bg-ds2026-badge-violet-bg', text: 'text-ds2026-badge-violet-text', dot: 'bg-ds2026-badge-violet-icon' }, // prettier-ignore
  purple: { bg: 'bg-ds2026-badge-purple-bg', text: 'text-ds2026-badge-purple-text', dot: 'bg-ds2026-badge-purple-icon' }, // prettier-ignore
  pink: { bg: 'bg-ds2026-badge-pink-bg', text: 'text-ds2026-badge-pink-text', dot: 'bg-ds2026-badge-pink-icon' }, // prettier-ignore
  rose: { bg: 'bg-ds2026-badge-rose-bg', text: 'text-ds2026-badge-rose-text', dot: 'bg-ds2026-badge-rose-icon' }, // prettier-ignore
  teal: { bg: 'bg-ds2026-badge-teal-bg', text: 'text-ds2026-badge-teal-text', dot: 'bg-ds2026-badge-teal-icon' }, // prettier-ignore
}
export const PALETTE_KEYS = Object.keys(LABEL_PALETTE) as LabelColorKey[]

/** Palette entry for a label's color key; unknown keys fall back to blue so a
 * server value the client doesn't know can never crash the pill. */
export function paletteFor(
  color: string
): (typeof LABEL_PALETTE)[LabelColorKey] {
  return LABEL_PALETTE[color as LabelColorKey] ?? LABEL_PALETTE.blue
}

/** Server errors carry {"detail": "..."} — surface it, else the fallback. */
export function toastApiError(e: unknown, fallback: string): void {
  const detail = axios.isAxiosError(e)
    ? (e.response?.data as { detail?: string } | undefined)?.detail
    : undefined
  toast.error(detail ?? fallback)
}

// ---------------------------------------------------------------------------
// Presentational
// ---------------------------------------------------------------------------
/** Colored label chip; renders a remove (X) button when `onRemove` is given. */
export function LabelPill({
  label,
  onRemove,
  size = 'sm',
}: {
  label: ApiReplyLabel
  onRemove?: () => void
  /** 'sm' for compact list rows; 'md' to match the classification-bar badges. */
  size?: 'sm' | 'md'
}) {
  const c = paletteFor(label.color)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full',
        size === 'md' ? 'px-2.5 py-1 text-sm' : 'px-1.5 py-0.5 text-sm',
        c.bg,
        c.text
      )}
    >
      {label.name}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="-mr-0.5 ml-0.5 rounded-full p-0.5 transition-colors hover:bg-current/20"
          aria-label={`Remove ${label.name}`}
        >
          <PiX className="size-3" />
        </button>
      )}
    </span>
  )
}

/**
 * Row-compact pill list: renders the first `max` labels and folds the rest
 * into a "+X more" chip (hidden names shown on hover), so threads with many
 * labels don't wrap the inbox row across several lines.
 */
export function LabelPillList({
  labels,
  max = 2,
}: {
  labels: ApiReplyLabel[]
  max?: number
}) {
  const visible = labels.slice(0, max)
  const hidden = labels.slice(max)
  return (
    <>
      {visible.map((l) => (
        <LabelPill key={l.id} label={l} />
      ))}
      {hidden.length > 0 && (
        <span
          className="inline-flex items-center rounded-full bg-gray-100 px-1.5 py-0.5 text-sm text-gray-600"
          title={hidden.map((l) => l.name).join(', ')}
        >
          +{hidden.length} more
        </span>
      )}
    </>
  )
}

export function ColorDot({ color }: { color: string }) {
  return (
    <span
      className={cn('size-2.5 shrink-0 rounded-full', paletteFor(color).dot)}
      aria-hidden
    />
  )
}

/**
 * Portals a menu to <body> and pins it (fixed) just above the trigger, so it
 * can't be clipped by the inbox's overflow-hidden panes. Opens upward because
 * these triggers sit in the classification bottom bar. Position is measured
 * from the anchor at render; the triggers live in non-scrolling bars (top
 * filter row + fixed bottom bar), so it stays aligned. A full-viewport backdrop
 * closes it on outside click. Children supply their own box styling.
 */
export function AnchoredMenu({
  anchor,
  onClose,
  align = 'left',
  children,
}: {
  anchor: HTMLElement
  onClose: () => void
  align?: 'left' | 'right'
  children: ReactNode
}) {
  const modalDepth = useModalDepth()
  const zIndex = useMemo(() => getDropdownZIndex(modalDepth), [modalDepth])
  // Close on Escape to match the app's other dropdowns (Radix ones get this
  // for free). The menu only mounts while open, so a mount-lifetime listener
  // is correct; the ref keeps the latest onClose without re-subscribing.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  useMountEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })
  const rect = anchor.getBoundingClientRect()
  const style: CSSProperties = {
    position: 'fixed',
    bottom: window.innerHeight - rect.top + 6,
    zIndex,
    ...(align === 'right'
      ? { right: window.innerWidth - rect.right }
      : { left: rect.left }),
  }
  return createPortal(
    <>
      {/* Backdrop shares the menu's z-index; the menu paints on top via DOM order. */}
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        tabIndex={-1}
        className="cursor-default"
        style={{ position: 'fixed', inset: 0, zIndex }}
      />
      <div style={style}>{children}</div>
    </>,
    document.body
  )
}

// ---------------------------------------------------------------------------
// State hook — real endpoints behind the same shape the prototype exposed.
// ---------------------------------------------------------------------------
export interface InboxLabelsApi {
  /** All of the company's labels (empty while loading). */
  labels: ApiReplyLabel[]
  /** InlineSelectFilter options with color-dot icons; values are String(id). */
  options: { label: string; value: string; icon?: ReactNode }[]
  toggleThreadLabel: (threadId: number | null, label: ApiReplyLabel) => void
  createLabel: (
    name: string,
    color: LabelColorKey,
    applyToThreadId: number | null
  ) => Promise<void>
  renameLabel: (labelId: number, name: string) => void
  deleteLabel: (labelId: number) => void
}

/**
 * Label CRUD + per-thread toggle backed by the real endpoints. Mutation
 * failures toast the server's detail message; the optimistic toggle patch
 * and rollback live in {@link useToggleThreadLabel}.
 */
export function useInboxLabels(): InboxLabelsApi {
  const { data } = useGetReplyLabels()
  const createMutation = useCreateReplyLabel()
  const updateMutation = useUpdateReplyLabel()
  const deleteMutation = useDeleteReplyLabel()
  const toggleMutation = useToggleThreadLabel()

  const labels = useMemo(() => data ?? [], [data])

  const options = useMemo(
    () =>
      labels.map((l) => ({
        label: l.name,
        value: String(l.id),
        icon: <ColorDot color={l.color} />,
      })),
    [labels]
  )

  return useMemo<InboxLabelsApi>(
    () => ({
      labels,
      options,
      toggleThreadLabel: (threadId, label) => {
        if (threadId == null) return
        toggleMutation.mutate(
          { threadId, label },
          {
            onError: (e) => toastApiError(e, 'Could not update the label.'),
          }
        )
      },
      createLabel: async (name, color, applyToThreadId) => {
        const trimmed = name.trim()
        if (!trimmed) return
        // Create-and-apply composes the two endpoints; a failed create
        // throws to the caller (the form stays open), a failed apply still
        // leaves a valid label behind.
        const created = await createMutation.mutateAsync({
          name: trimmed,
          color,
        })
        if (applyToThreadId != null) {
          toggleMutation.mutate(
            { threadId: applyToThreadId, label: created },
            {
              onError: (e) => toastApiError(e, 'Could not apply the label.'),
            }
          )
        }
      },
      renameLabel: (labelId, name) => {
        const trimmed = name.trim()
        if (!trimmed) return
        updateMutation.mutate(
          { id: labelId, name: trimmed },
          {
            onError: (e) => toastApiError(e, 'Could not rename the label.'),
          }
        )
      },
      deleteLabel: (labelId) => {
        deleteMutation.mutate(labelId, {
          onError: (e) => toastApiError(e, 'Could not delete the label.'),
        })
      },
    }),
    [
      labels,
      options,
      createMutation,
      updateMutation,
      deleteMutation,
      toggleMutation,
    ]
  )
}

// ---------------------------------------------------------------------------
// Inline apply UI for the classification bottom bar
// ---------------------------------------------------------------------------
/**
 * "Labels: [pills] [+ Add label]" strip for the classification bottom bar.
 * The Add-label menu handles apply/unapply, search, inline rename/delete,
 * and creating a new label (name + palette color) applied to `threadId`.
 * `applied` comes from the thread's list row (`reply.labels`), which the
 * optimistic toggle patch keeps current.
 */
export function ThreadLabelsInline({
  threadId,
  applied,
  api,
}: {
  threadId: number | null
  applied: ApiReplyLabel[]
  api: InboxLabelsApi
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [search, setSearch] = useState('')
  const [searchFocused, setSearchFocused] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState<LabelColorKey>('blue')
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingName, setEditingName] = useState('')

  const appliedIds = applied.map((l) => l.id)
  const visible = api.labels.filter((l) =>
    l.name.toLowerCase().includes(search.trim().toLowerCase())
  )

  const close = () => {
    setAnchorEl(null)
    setSearch('')
    setCreating(false)
    setEditingId(null)
  }

  // Shared by the Enter key and the save/create buttons of each inline form.
  const commitRename = (labelId: number) => {
    api.renameLabel(labelId, editingName)
    setEditingId(null)
  }
  const submitCreate = async () => {
    try {
      await api.createLabel(newName, newColor, threadId)
      setNewName('')
      setNewColor('blue')
      setCreating(false)
    } catch (e) {
      // Keep the form open (with the typed name) so the user can adjust —
      // e.g. after a duplicate-name 400.
      toastApiError(e, 'Could not create the label.')
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="h-4 w-px bg-line" />
      <span className="text-sm text-gray-600">Labels:</span>
      {applied.map((l) => (
        <LabelPill
          key={l.id}
          label={l}
          size="md"
          onRemove={() => api.toggleThreadLabel(threadId, l)}
        />
      ))}
      <button
        type="button"
        onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
        className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-2.5 py-1 text-sm text-gray-600 transition-colors hover:bg-gray-100"
      >
        <PiPlus className="size-3.5" />
        Add label
      </button>
      {anchorEl && (
        <AnchoredMenu anchor={anchorEl} onClose={close} align="left">
          <div className="w-72 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg">
            <div className="mb-2">
              <FilterSearchInput
                value={search}
                onChange={setSearch}
                isFocused={searchFocused}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                placeholder="Search labels..."
              />
            </div>

            <div className="max-h-56 overflow-y-auto">
              {visible.length === 0 && !creating && (
                <p className="px-2 py-3 text-center text-xs text-gray-400">
                  No labels found
                </p>
              )}
              {visible.map((l) => {
                const isApplied = appliedIds.includes(l.id)
                const isEditing = editingId === l.id
                return (
                  <div
                    key={l.id}
                    className="group flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-neutral-50"
                  >
                    {isEditing ? (
                      <>
                        <ColorDot color={l.color} />
                        <input
                          type="text"
                          autoFocus
                          aria-label="Rename label"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename(l.id)
                          }}
                          className="min-w-0 flex-1 rounded border border-neutral-300 px-1.5 py-0.5 text-sm focus:border-brand focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => commitRename(l.id)}
                          className="rounded p-1 text-brand hover:bg-neutral-100"
                          aria-label="Save"
                        >
                          <PiCheck className="size-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => api.toggleThreadLabel(threadId, l)}
                          className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        >
                          <Checkbox
                            checked={isApplied}
                            readOnly
                            tabIndex={-1}
                            aria-hidden
                            wrapperClassName="pointer-events-none flex-shrink-0"
                          />
                          <ColorDot color={l.color} />
                          <span className="truncate text-sm text-gray-800">
                            {l.name}
                          </span>
                        </button>
                        <div className="flex items-center gap-0.5 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(l.id)
                              setEditingName(l.name)
                            }}
                            className="rounded p-1 hover:bg-neutral-200 hover:text-gray-600"
                            aria-label={`Rename ${l.name}`}
                          >
                            <PiPencilSimple className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => api.deleteLabel(l.id)}
                            className="rounded p-1 hover:bg-neutral-200 hover:text-error-strong"
                            aria-label={`Delete ${l.name}`}
                          >
                            <PiTrash className="size-3.5" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )
              })}
            </div>

            <div className="mt-1 border-t border-neutral-200 pt-2">
              {creating ? (
                <div className="px-1 pb-1">
                  <input
                    type="text"
                    autoFocus
                    aria-label="New label name"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void submitCreate()
                    }}
                    placeholder="Label name"
                    className="mb-2 w-full rounded-[6px] border border-line bg-white px-2 py-1.5 text-sm text-fg outline-none placeholder-fg-muted focus:border-brand-active"
                  />
                  <div className="mb-2 flex flex-wrap items-center gap-1.5">
                    {PALETTE_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setNewColor(key)}
                        aria-label={`Color ${key}`}
                        className={cn(
                          'size-5 rounded-full ring-offset-1 transition',
                          LABEL_PALETTE[key].dot,
                          newColor === key
                            ? 'ring-2 ring-brand'
                            : 'hover:opacity-80'
                        )}
                      />
                    ))}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <FbButton
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setCreating(false)
                        setNewName('')
                      }}
                    >
                      Cancel
                    </FbButton>
                    <FbButton
                      size="sm"
                      type="button"
                      className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!border-neutral-100 disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:hover:!bg-neutral-100"
                      disabled={!newName.trim()}
                      onClick={() => void submitCreate()}
                    >
                      Create
                    </FbButton>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setCreating(true)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-brand-active hover:bg-neutral-100"
                >
                  <PiPlus className="size-4" />
                  Create new label
                </button>
              )}
            </div>
          </div>
        </AnchoredMenu>
      )}
    </div>
  )
}
